import { useEffect, useState } from "react";
import Timeago from "../_Shared/Timeago";
import { ActionIcon, Table, Title, Paper, Group, Button } from "@mantine/core";
import { IconFingerprint, IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { jsonDelete, jsonGet, jsonPost } from "../api";
import { arrayBufferToBase64Url, base64UrlDecode } from "./base64helpers";
import { GetUserAgentString } from "../UserAgentDetails";

export default function PasskeyRegister() {
  const [keys, setKeys] = useState([]);
  const [isRegistering, setIsRegistering] = useState(false);
  const { t } = useTranslation("FIDO");
  const icon = <IconFingerprint size={18} />;

  // Fetch existing passkeys
  const fetchKeys = async () => {
    try {
      const data = await jsonGet("auth/passkey");
      return data || [];
    } catch (error) {
      console.error("Error fetching passkeys:", error);
      notifications.show({
        title: t("fidoKey:fetchError"),
        message: error.message,
        color: "red",
      });
      return [];
    }
  };

  // Delete a passkey
  const deleteKey = async ({ keyId }) => {
    try {
      await jsonDelete(`auth/passkey/keys/${keyId}`);
      // Refresh list after deletion
      const updatedKeys = await fetchKeys();
      setKeys(updatedKeys);
      notifications.show({
        title: t("fidoKey:deletedSuccess"),
        message: t("fidoKey:keyRemoved"),
        color: "green",
      });
    } catch (error) {
      console.error("Error deleting passkey:", error);
      notifications.show({
        title: t("fidoKey:deleteError"),
        message: error.message,
        color: "red",
      });
    }
  };

  // Register a new passkey
  const registerKey = async () => {
    if (isRegistering) return;

    try {
      setIsRegistering(true);

      // 1. Get registration options from the server
      const optionsResponse = await jsonPost("auth/passkey/register/begin");

      // 2. Convert base64url strings to ArrayBuffers for the navigator.credentials.create call
      const publicKeyOptions = {
        challenge: base64UrlDecode(optionsResponse.challenge),
        rp: {
          name: optionsResponse.rp.name,
          id: optionsResponse.rp.id,
        },
        user: {
          id: base64UrlDecode(optionsResponse.user.id),
          name: optionsResponse.user.name,
          displayName: optionsResponse.user.displayName,
        },
        pubKeyCredParams: optionsResponse.pubKeyCredParams,
        authenticatorSelection: optionsResponse.authenticatorSelection,
        timeout: optionsResponse.timeout,
        attestation: optionsResponse.attestation,
        excludeCredentials: (optionsResponse.excludeCredentials || []).map(
          (credential) => ({
            id: base64UrlDecode(credential.id),
            type: credential.type,
            transports: credential.transports || ["internal"],
          })
        ),
      };

      console.log("Exclude credentials:", publicKeyOptions.excludeCredentials);

      // 3. Create the credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      });

      // 4. Convert the credential for sending to the server
      const credentialResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        response: {
          attestationObject: arrayBufferToBase64Url(
            credential.response.attestationObject
          ),
          clientDataJSON: arrayBufferToBase64Url(
            credential.response.clientDataJSON
          ),
          transports: credential.response.getTransports
            ? credential.response.getTransports()
            : [],
        },
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults(),
      };

      // 5. Send the credential to the server for verification
      await jsonPost("auth/passkey/register/complete", {
        deviceName: GetUserAgentString(),
        attestationResponse: credentialResponse,
      });

      // 6. Refresh the key list
      const updatedKeys = await fetchKeys();
      setKeys(updatedKeys);

      notifications.show({
        title: t("fidoKey.registeredSuccess"),
        message: t("fidoKey.keyAdded"),
        color: "green",
      });
    } catch (error) {
      console.error("Passkey registration error:", error);
      notifications.show({
        title: t("fidoKey:registrationError"),
        message: error.message,
        color: "red",
      });
    } finally {
      setIsRegistering(false);
    }
  };

  useEffect(() => {
    fetchKeys().then((data) => setKeys(data));
  }, []);

  return (
    <Paper p="md">
      <Title order={3}>{t("fidoKey.title_passkeys")}</Title>
      <Table>
        <Table.Tbody>
          {keys &&
            keys.map((key, index) => {
              const usageInfo = (
                <>
                  {/* 
                      Nesting component in text complicates getting a good split for translation. 
                      The current split may assume too much about sentence structure  
                    */}
                  {t("fidoKey.lastUsed")} {<Timeago date={key.lastUsed} />}
                  {", "}
                  {t("fidoKey.created")} {<Timeago date={key.created} />}
                </>
              );

              return (
                <Table.Tr key={`session-${index}`}>
                  <Table.Td>
                    <Group>
                      {icon} {key.deviceName}
                    </Group>
                  </Table.Td>
                  <Table.Td>{usageInfo}</Table.Td>
                  <Table.Td ta="right">
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => deleteKey({ keyId: key.id })}
                      aria-label={t("fidoKey.actions.remove")}
                      aria-haspopup={true}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              );
            })}

          <Table.Tr>
            <Table.Td colSpan={3} ta="center">
              <Group justify="center">
                {/* {icon} */}
                <Button
                  variant="default"
                  leftSection={icon}
                  onClick={() => registerKey()}
                  loading={isRegistering}
                  aria-label={t("fidoKey.actions.add")}
                  aria-haspopup={true}
                >
                  {t("fidoKey.actions.add")}
                </Button>
                {/* <ActionIcon>
                <IconPlus size={16} />
              </ActionIcon> */}
              </Group>
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
